import React, { useEffect, useState } from "react";
import { withTranslation } from "react-i18next";
import Page from "components/Page";
import Text from "components/Text";
import Button from "components/Button";
import Card from "components/Card";
import constants from "../../../../../../constants";
import styles from "../reviewRating.module.scss";
import {
  commentAddedBySuperAdmin,
  exportChat,
  seenChatReponseRating,
} from "repositories/ai-assistant-repository";
import { formatLocalDateTime, handleError, handleSuccess } from "utils";
import CustomRating from "components/CustomRating";
import Loader from "components/Loader";

const InfoBlock = ({ label, value, lineHeight = "14px" }) => (
  <div>
    <Text size="12px" weight="400" color="#6F7788" lineHeight="12px">
      {label}
    </Text>
    <Text
      size="14px"
      weight="600"
      color="#102C42"
      lineHeight={lineHeight}
      marginTop="10px"
    >
      <span style={{ whiteSpace: "pre-line", overflowWrap: "break-word" }}>
        {value}
      </span>
    </Text>
  </div>
);

const RatingResponseView = ({ t, history, location }) => {
  const { state } = location;
  const [comment, setComment] = useState(state?.commentBySuperAdmin);
  const [isLoading, setIsLoading] = useState(false);
  const [showLoader, setShowLoader] = useState(false);

  useEffect(() => {
    if (state?.reviewStatus == "Seen") return;
    seenRatingDetail();
  }, []);

  const seenRatingDetail = async () => {
    if (!state?.id) return;
    try {
      await seenChatReponseRating(state?.id);
    } catch (error) {
      handleError(error);
    }
  };

  const goBack = () => {
    history.push(constants.routes.aiAssistant.ratingResponses);
  };

  const handleExport = async (session) => {
    try {
      setShowLoader(true);
      const params = {
        AiChatSessionId: session?.aiAssistantSessionId,
        AiAssistantMessageId: session?.userMessageId,
        ExportWholeChat: false,
      };
      const resp = await exportChat(params);

      // Handle file download if base64Content is provided
      if (resp?.data?.base64Content && resp?.data?.fileName) {
        const { base64Content, fileName, contentType } = resp.data;

        // Convert base64 to blob
        const byteCharacters = atob(base64Content);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: contentType });

        // Create download link and trigger download
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        handleSuccess(t("aiAssistant.fileDownloadedSuccessfully"));
      } else {
        handleSuccess(resp?.message || t("aiAssistant.exportCompleted"));
      }
    } catch (error) {
      handleError(error?.message || "");
    } finally {
      setShowLoader(false);
    }
  };

  const infoData = [
    {
      label: t("aiAssistant.ratingReviews.username"),
      value: `${state?.user?.firstName} ${state?.user?.lastName}`,
    },
    {
      label: t("aiAssistant.ratingReviews.email"),
      value: state?.owner?.emailId,
    },
    {
      label: t("aiAssistant.ratingReviews.accountOwneradmin"),
      value: `${state?.owner?.firstName} ${state?.owner?.lastName}`,
    },

    {
      label: t("aiAssistant.ratingReviews.date"),
      value: formatLocalDateTime(state?.createdAt, "MMM DD, YYYY"),
    },
    {
      label: t("aiAssistant.ratingReviews.queryAsked"),
      value: state?.userMessage,
    },
    {
      label: t("aiAssistant.ratingReviews.responseReceived"),
      value: state?.aiResponse,
      lineHeight: "20px",
    },
  ];

  const commentGivenBySuperAdmin = async () => {
    try {
      setIsLoading(true);
      const params = {
        id: state?.id,
        commentBySuperAdmin: comment,
      };

      const result = await commentAddedBySuperAdmin(params);
      handleSuccess(result?.message);
      goBack();
    } catch (error) {
      handleError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const renderHeader = () => (
    <div className="content_wrapper_pms">
      <div>
        <h2 className="title pms_heading page_title_margin_top page_title_margin_bottom">
          {t("aiAssistant.ratingReview")}
        </h2>
        <Text size="14px" weight="300" color="#000000">
          {t("aiAssistant.ratingReviews.aiAssistSubheading")}
        </Text>
      </div>
      <Button onClick={() => handleExport(state)}>{t("export")}</Button>
    </div>
  );

  return (
    <Page onBack={goBack}>
      {(isLoading || showLoader) && <Loader />}
      {renderHeader()}
      <Card className={styles.response_view_card}>
        <div className={styles.content_container}>
          {infoData.map((item, idx) => (
            <InfoBlock
              key={idx}
              label={item.label}
              value={item.value}
              lineHeight={item.lineHeight}
            />
          ))}

          <div>
            <Text size="12px" weight="400" color="#6F7788" lineHeight="12px">
              {t("aiAssistant.ratingReviews.ratingGivenByUser")}
            </Text>
            <div className="mt-1">
              <CustomRating
                value={state?.rating}
                readOnly={true}
                onChange={() => {}}
                size={24}
                spacing={6}
                allowHalfStars={false}
              />
            </div>
          </div>

          <InfoBlock
            label={t("aiAssistant.ratingReviews.additionalComment")}
            value={state?.commentByUser || "--"}
          />
          <div>
            <Text size="13px" weight="400" color="#79869A" lineHeight="14px">
              {t("aiAssistant.ratingReviews.addComment")}
            </Text>
            <Text
              size="11px"
              weight="500"
              color="#79869A"
              marginBottom="6px"
              marginTop="4px"
            >
              {t("aiAssistant.ratingReviews.commentNotVisible")}
            </Text>
            <div className="c-field">
              <textarea
                className="c-form-control bg-transparent"
                name="comment"
                placeholder={t("taskManagement.addComment")}
                maxLength={1000}
                rows="5"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
              />
              <span className={styles["character-remaining-msg"]}>
                {`${t("aiAssistant.ratingReviews.characterRemaining")} ${
                  1000 - (comment?.length || 0)
                }/1000`}
              </span>
            </div>
          </div>
        </div>

        <div>
          <button
            className="button button-round button-shadow mr-md-4 mb-3 w-sm-100"
            onClick={() => commentGivenBySuperAdmin()}
          >
            {t("save")}
          </button>
          <button
            className="button button-round button-border button-dark btn-mobile-link"
            onClick={goBack}
          >
            {t("cancel")}
          </button>
        </div>
      </Card>
    </Page>
  );
};

export default withTranslation()(RatingResponseView);
